import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import moneyCurrency2 from "../assets/money-currency2.png";
import check from "../assets/check.png";
import star from "../assets/star.png";
import calendar from "../assets/calendar.png";
import rupeeIcon from "../assets/rupees.png";
import { useAuth } from "../context/auth-context";

const PaymentHistorySupervisor = () => {
  const navigate = useNavigate();
  const { id: workerId } = useParams();
  const { token } = useAuth();

  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [summary, setSummary] = useState({
    totalAmount: 0,
    totalNetAmount: 0,
    attendancePayout: 0,
    incentivePayout: 0,
    shiftCount: 0,
  });
  const [accountDetails, setAccountDetails] = useState({ accountNumber: "" });
  const loadingRef = useRef(null);
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const observerRef = useRef(null);

  const getCurrentMonthYear = () => {
    const now = new Date();
    return `${now.getFullYear()}-${now.getMonth() + 1}`;
  };

  const [selectedPeriod, setSelectedPeriod] = useState(getCurrentMonthYear());

//   const formatAccountNumber = (accountNo) => {
//     if (!accountNo) return "-";
//     const first = accountNo.charAt(0);
//     const last4 = accountNo.slice(-4);
//     return `${first}${"X".repeat(accountNo.length - 5)}${last4}`;
//   };
// const fetchAccountDetails = useCallback(async () => {
//   if (!workerId) return;
//   try {
//     const response = await axios.get(`${BASE_URL}/lenskart/account-details/${workerId}`, {
//       headers: { Authorization: `Bearer ${token}` },
//     });
//     if (response.data?.accountNumber) setAccountDetails(response.data);
//   } catch (error) {
//     console.error("Error fetching account details:", error);
//   }
// }, [workerId, token, BASE_URL]);
  const formatDateTime = (isoString) => {
    if (!isoString) return "-";
    const dateObj = new Date(isoString);
    const day = dateObj.getUTCDate().toString().padStart(2, "0");
    const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    const month = monthNames[dateObj.getUTCMonth()];
    const year = String(dateObj.getUTCFullYear()).slice(-2);
    let hours = dateObj.getUTCHours();
    const minutes = dateObj.getUTCMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return `${day} ${month}'${year} | ${hours}:${minutes} ${ampm}`;
  };

  const formatPaidOn = (isoString) => {
    if (!isoString) return null;
    const dateObj = new Date(isoString);
    const day = dateObj.getUTCDate();
    const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    const month = monthNames[dateObj.getUTCMonth()];
    const year = String(dateObj.getUTCFullYear()).slice(-2);
    return `${day} ${month}'${year}`;
  };

const fetchPayments = useCallback(
  async (page = 1, reset = false) => {
    if (loading || !workerId) return;
    try {
      setLoading(true);
      let params = { page };
      if (selectedPeriod !== "all") {
        const [year, month] = selectedPeriod.split("-");
        const startDate = new Date(year, month - 1, 1).toLocaleDateString("en-CA");
        const endDate = new Date(year, month, 0).toLocaleDateString("en-CA");
        params = { ...params, startDate, endDate };
      }
      const response = await axios.get(`${BASE_URL}/lenskart/payment-history/${workerId}`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      const { data } = response.data;
      setPayments((prev) => (reset ? data.payments : [...prev, ...data.payments]));
      setSummary(data.summary);
      setHasNextPage(data.pagination.hasNextPage);
      setCurrentPage(data.pagination.currentPage);
    } catch (error) {
      console.error("Error fetching payments:", error);
    } finally {
      setLoading(false);
    }
  },
  [selectedPeriod, token, BASE_URL, loading, workerId]
);

//   useEffect(() => {
//     fetchAccountDetails();
//   }, [fetchAccountDetails]);

  useEffect(() => {
    setPayments([]);
    fetchPayments(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeriod, workerId]);

  useEffect(() => {
    if (observerRef.current && loadingRef.current)
      observerRef.current.unobserve(loadingRef.current);
    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !loading)
          fetchPayments(currentPage + 1);
      },
      { threshold: 0.1 }
    );
    if (loadingRef.current && hasNextPage)
      observerRef.current.observe(loadingRef.current);
    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [hasNextPage, loading, currentPage, fetchPayments]);

  const generateMonthYearOptions = () => {
    const options = [{ value: "all", label: "All Periods" }];
    const startDate = new Date(2025, 0);
    const currentDate = new Date();
    for (let date = new Date(startDate); date <= currentDate; date.setMonth(date.getMonth() + 1)) {
      options.push({
        value: `${date.getFullYear()}-${date.getMonth() + 1}`,
        label: `${date.toLocaleString("default", { month: "short" })}, ${date.getFullYear()}`,
      });
    }
    return options;
  };

  const getStatus = (status) => {
    const s = status?.toLowerCase();
    if (s === "success" || s === "successful") return { label: "Successful", color: "#16a34a", dot: "#16a34a", bg: "transparent" };
    if (s === "pending") return { label: "Pending", color: "#d97706", dot: "#d97706", bg: "transparent" };
    if (s === "failed") return { label: "Failed", color: "#dc2626", dot: "#dc2626", bg: "transparent" };
    if (s === "payoutgenerated") return { label: "Payout Generated", color: "#2563eb", dot: "#2563eb", bg: "transparent" };
    return { label: status, color: "#6b7280", dot: "#6b7280", bg: "transparent" };
  };

  const styles = {
    page: {
      minHeight: "100vh",
      backgroundColor: "#f3f4f6",
      paddingTop: "80px",
      fontFamily: "'Nunito', 'Segoe UI', sans-serif",
    },
    container: {
      maxWidth: "480px",
      margin: "0 auto",
      backgroundColor: "#f3f4f6",
      paddingBottom: "32px",
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "16px 16px 8px",
    },
    totalShifts: {
      fontWeight: "700",
      fontSize: "15px",
      color: "#111827",
    },
    selectWrapper: {
      position: "relative",
    },
    select: {
      appearance: "none",
      backgroundColor: "#fff",
      border: "1.5px solid #e5e7eb",
      borderRadius: "10px",
      padding: "8px 32px 8px 14px",
      fontSize: "14px",
      fontWeight: "600",
      color: "#111827",
      cursor: "pointer",
      outline: "none",
      boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    },
    selectArrow: {
      position: "absolute",
      right: "10px",
      top: "50%",
      transform: "translateY(-50%)",
      pointerEvents: "none",
      fontSize: "12px",
      color: "#6b7280",
    },
    summaryCard: {
      margin: "0 12px 16px",
      background: "linear-gradient(135deg, #eef0ff 0%, #e8eaff 100%)",
      borderRadius: "18px",
      padding: "20px",
      position: "relative",
      overflow: "hidden",
    },
    summaryIconRow: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    summaryIcon: {
      width: "36px",
      height: "36px",
      borderRadius: "10px",
      backgroundColor: "#fff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "18px",
      boxShadow: "0 1px 4px rgba(99,102,241,0.15)",
    },
    summaryLabel: {
      fontSize: "13px",
      color: "#5E5E5E",
      fontWeight: "600",
      letterSpacing: "0.02em",
    },
    summaryAmount: {
      fontSize: "22px",
      fontWeight: "800",
      color: "#3C3C3C",
      letterSpacing: "-0.5px",
      display: "flex",
      marginBottom: "4px",
    },
    summaryAccount: {
      fontSize: "13px",
      color: "#222222",
      fontWeight: "500",
      marginBottom: "16px",
    },
    summarySubRow: {
      display: "flex",
      gap: "20px",
    },
    summarySubItem: {
      display: "flex",
      flexDirection: "column",
      gap: "3px",
    },
    summarySubIcon: {
      fontSize: "20px",
      color: "#6366f1",
      marginBottom: "2px",
    },
    summarySubLabel: {
      fontSize: "12px",
      color: "#303030",
      fontWeight: "500",
    },
    summarySubAmount: {
      fontSize: "16px",
      fontWeight: "700",
      color: "#3C3C3C",
    },
    list: {
      display: "flex",
      flexDirection: "column",
      gap: "12px",
      padding: "0 12px",
    },
    card: {
      backgroundColor: "#fff",
      borderRadius: "16px",
      padding: "16px",
      boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
    },
    statusRow: {
      display: "flex",
      alignItems: "center",
      gap: "6px",
      marginBottom: "10px",
    },
    statusDot: (color) => ({
      width: "8px",
      height: "8px",
      borderRadius: "50%",
      backgroundColor: color,
      flexShrink: 0,
    }),
    statusText: (color) => ({
      fontSize: "13px",
      fontWeight: "700",
      color,
    }),
    errorBanner: {
      backgroundColor: "#fef2f2",
      border: "1px solid #fecaca",
      borderRadius: "8px",
      padding: "8px 12px",
      fontSize: "13px",
      color: "#dc2626",
      fontWeight: "500",
      marginBottom: "10px",
    },
    cardMainRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      marginBottom: "4px",
    },
    companyName: {
      fontSize: "17px",
      fontWeight: "800",
      color: "#111827",
    },
    amount: (color) => ({
      fontSize: "17px",
      fontWeight: "800",
      color,
    }),
    txnRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "12px",
    },
    txnId: {
      fontSize: "12px",
      color: "#9ca3af",
      fontWeight: "500",
    },
    paidOn: {
      fontSize: "12px",
      color: "#9ca3af",
      fontWeight: "500",
    },
    divider: {
      height: "1px",
      backgroundColor: "#f3f4f6",
      marginBottom: "12px",
    },
    dateTimeRow: {
      display: "flex",
      justifyContent: "space-between",
      gap: "12px",
    },
    dateTimeBlock: {
      flex: 1,
    },
    dateTimeLabel: {
      display: "flex",
      alignItems: "center",
      gap: "5px",
      fontSize: "11px",
      color: "#9ca3af",
      fontWeight: "500",
      marginBottom: "4px",
    },
    dateTimeValue: {
      fontSize: "13px",
      fontWeight: "700",
      color: "#111827",
    },
    emptyState: {
      textAlign: "center",
      padding: "40px 16px",
      color: "#9ca3af",
      fontSize: "14px",
    },
    loadingText: {
      textAlign: "center",
      padding: "16px",
      color: "#9ca3af",
      fontSize: "14px",
    },
    summaryBorderBox: {
      width: "24px",
      height: "24px",
      borderRadius: "6px",
      backgroundColor: "#ffffff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    summaryStarIcon: {
      width: "24px",
      height: "24px",
      borderRadius: "6px",
      backgroundColor: "#ffffff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
  };

  const renderEmptyState = () => (
    <div style={styles.emptyState}>
      {selectedPeriod === "all" ? "This worker hasn't made any payments yet" : "No payments found for the selected period"}
    </div>
  );

  const totalAmount = summary.totalNetAmount ?? summary.totalAmount ?? 0;

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        {/* Header */}
        <div style={{ textAlign: "center", padding: "8px 16px 4px" }}>
          <div className="relative flex items-center gap-4 justify-center mb-6">
            <button onClick={() => navigate(-1)} className="text-4xl absolute left-0">‹</button>
            <h2 className="text-xl font-bold text-center">Payment History</h2>
          </div>
        </div>

        {/* Total Shifts + Period Selector */}
        <div style={styles.header}>
          <span style={styles.totalShifts}>
            Total Shifts : {summary.shiftCount ?? "-"}
          </span>
          <div style={styles.selectWrapper}>
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              style={styles.select}
            >
              {generateMonthYearOptions().map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <span style={styles.selectArrow}>▼</span>
          </div>
        </div>

        {/* Summary Card */}
        <div style={styles.summaryCard}>
          <img
            src={rupeeIcon}
            alt="₹"
            style={{
              position: "absolute",
              right: "-10px",
              top: "50%",
              transform: "translateY(-50%)",
              width: "180px",
              height: "180px",
              opacity: 0.8,
              pointerEvents: "none",
              userSelect: "none",
              filter: "brightness(0) invert(1)",
              objectFit: "contain",
            }}
          />
          <div style={styles.summaryIconRow}>
            <div style={styles.summaryIcon}><img src={moneyCurrency2} alt="Money Currency" /></div>
            <div>
              <span style={styles.summaryLabel}>Total Amount Paid</span>
              <div style={styles.summaryAmount}>₹ {totalAmount.toFixed(2)}</div>
            </div>
          </div>
          <div style={styles.summarySubRow}>
            <div style={styles.summarySubItem}>
              <div style={styles.summaryBorderBox}><span style={styles.summarySubIcon}><img src={check} alt="" /></span></div>
              <span style={styles.summarySubLabel}>Attendance Payout</span>
              <span style={styles.summarySubAmount}>₹ {(summary.attendancePayout ?? 0).toFixed(2)}</span>
            </div>
            <div style={styles.summarySubItem}>
              <div style={styles.summaryStarIcon}><span style={styles.summarySubIcon}><img src={star} alt="" /></span></div>
              <span style={styles.summarySubLabel}>Incentive Payout</span>
              <span style={styles.summarySubAmount}>₹ {(summary.incentivePayout ?? 0).toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Payment Cards */}
        <div style={styles.list}>
          {!loading && payments.length === 0 ? (
            renderEmptyState()
          ) : (
            <>
              {payments.map((payment) => {
                const statusInfo = getStatus(payment.status || "");
                const isFailed = payment.status?.toLowerCase() === "failed";
                const isPending = payment.status?.toLowerCase() === "pending";
                const paidOnStr = formatPaidOn(payment.paidOn || payment.updatedAt);
                const amountColor = isFailed ? "#dc2626" : isPending ? "#d97706" : "#16a34a";
                const amount = payment.netAmount ?? payment.amount ?? 0;

                return (
                  <div key={payment.id || payment._id} style={styles.card}>
                    <div style={styles.statusRow}>
                      <div style={styles.statusDot(statusInfo.dot)} />
                      <span style={styles.statusText(statusInfo.color)}>{statusInfo.label}</span>
                    </div>

                    {isFailed && payment.failureReason && (
                      <div style={styles.errorBanner}>{payment.failureReason}</div>
                    )}

                    <div style={styles.cardMainRow}>
                      <span style={styles.companyName}>
                        {payment.companyName
                          ? `${payment.companyName.charAt(0).toUpperCase()}${payment.companyName.slice(1).toLowerCase()} (${payment.location || "-"})`
                          : "-"}
                      </span>
                      <span style={styles.amount(amountColor)}>
                        ₹ {amount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>

                    <div style={styles.txnRow}>
                      <span style={styles.txnId}>
                        {payment.referenceId ? `Transaction ID : ${payment.referenceId}` : ""}
                      </span>
                      {paidOnStr && !isPending && (
                        <span style={styles.paidOn}>Paid on : {paidOnStr}</span>
                      )}
                    </div>

                    <div style={styles.divider} />

                    <div style={styles.dateTimeRow}>
                      <div style={styles.dateTimeBlock}>
                        <div style={styles.dateTimeLabel}>
                          <img src={calendar} alt="Calendar" /> Entry Date &amp; Time
                        </div>
                        <div style={styles.dateTimeValue}>{formatDateTime(payment.entryTime)}</div>
                      </div>
                      <div style={styles.dateTimeBlock}>
                        <div style={styles.dateTimeLabel}>
                          <img src={calendar} alt="Calendar" /> Exit Date &amp; Time
                        </div>
                        <div style={styles.dateTimeValue}>{formatDateTime(payment.exitTime)}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
              {hasNextPage && <div ref={loadingRef} style={{ height: "20px" }} />}
            </>
          )}
          {loading && <div style={styles.loadingText}>Loading...</div>}
        </div>
      </div>
    </div>
  );
};

export default PaymentHistorySupervisor;